import { cssVar, Label, useAlignments, useScreen, useSizes } from 'metrix-common3';
// biome-ignore lint/correctness/noUnusedImports: React must be in scope when using JSX
import React from 'react';
import styled from 'styled-components';
import DeleteIcon from './DeleteIcon';
import EditIcon from './EditIcon';
import HierarchyIcon from './HierarchyIcon';

const CardWrapper = styled.div`
    display: flex;
    align-items: ${({ $mobile }) => ($mobile ? 'flex-start' : 'center')};
    margin-bottom: 8px;
    width: 100%;
    max-width: 100%;
`;

const Card = styled.div`
    display: flex;
    flex-direction: ${({ $mobile }) => ($mobile ? 'column' : 'row')};
    align-items: ${({ $mobile }) => ($mobile ? 'stretch' : 'center')};
    padding: 10px 16px;
    gap: ${({ $mobile }) => ($mobile ? '4px' : '24px')};
    background: ${cssVar.colorLightMode};
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
    flex: 1;
    min-width: 0;
    ${({ $nested }) =>
        $nested &&
        `
        background: ${cssVar.colorStrokeVeryLight};
    `}
`;

const HierarchyIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    min-width: 40px;
`;

const LabelArea = styled.div`
    width: ${({ $mobile }) => ($mobile ? '100%' : 'auto')};
    flex: ${({ $mobile }) => ($mobile ? 'none' : '1')};
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ValueRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    width: 100%;
`;

const ValueArea = styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
`;

const ActionArea = styled.div`
    width: 80px;
    min-width: 80px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    align-items: center;
`;

const IconButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;

    &:hover {
        background: ${cssVar.colorStrokeVeryLight};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
`;

const AddButton = styled(IconButton)`
    font-size: 24px;
    font-weight: 300;
    color: ${cssVar.colorPrimary};
`;

const EmptyCard = styled(Card)`
    border-style: dashed;
`;

const AdjustmentCard = ({ label, value, nested = false, onEdit, onDelete, onAdd, isEmpty = false }) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const { isMobile: mobile } = useScreen();

    if (isEmpty) {
        return (
            <CardWrapper $mobile={mobile}>
                <EmptyCard $nested={nested}>
                    <LabelArea />
                    <ValueArea />
                    <ActionArea>
                        {onAdd && (
                            <AddButton onClick={onAdd} title='Add'>
                                +
                            </AddButton>
                        )}
                    </ActionArea>
                </EmptyCard>
            </CardWrapper>
        );
    }

    const valueColor = nested ? cssVar.colorPrimaryText : cssVar.colorStrokeVeryDark;

    return (
        <CardWrapper $mobile={mobile}>
            {nested && (
                <HierarchyIconWrapper>
                    <HierarchyIcon />
                </HierarchyIconWrapper>
            )}
            <Card $nested={nested} $mobile={mobile}>
                <LabelArea $mobile={mobile} title={label}>
                    <Label text={label} size={sizes.lg} textAlign={alignments.left} />
                </LabelArea>
                {mobile ? (
                    <ValueRow>
                        <ValueArea>
                            <Label
                                text={value}
                                textAlign={alignments.right}
                                size='custom'
                                fontSize='22'
                                fontWeight='700'
                                lineHeight='110%'
                                color={valueColor}
                            />
                        </ValueArea>
                        <ActionArea>
                            {!nested && onAdd && (
                                <AddButton onClick={onAdd} title='Add adjustment'>
                                    +
                                </AddButton>
                            )}
                            {nested && (
                                <>
                                    {onDelete && (
                                        <IconButton onClick={onDelete} title='Delete'>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                    {onEdit && (
                                        <IconButton onClick={onEdit} title='Edit'>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </ActionArea>
                    </ValueRow>
                ) : (
                    <>
                        <ValueArea>
                            <Label
                                text={value}
                                textAlign={alignments.right}
                                size='custom'
                                fontSize='22'
                                fontWeight='700'
                                lineHeight='110%'
                                color={valueColor}
                            />
                        </ValueArea>
                        <ActionArea>
                            {!nested && onAdd && (
                                <AddButton onClick={onAdd} title='Add adjustment'>
                                    +
                                </AddButton>
                            )}
                            {nested && (
                                <>
                                    {onDelete && (
                                        <IconButton onClick={onDelete} title='Delete'>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                    {onEdit && (
                                        <IconButton onClick={onEdit} title='Edit'>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </ActionArea>
                    </>
                )}
            </Card>
        </CardWrapper>
    );
};

export default AdjustmentCard;
